import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { orgApi } from "../../lib/org";
import { iconSvg, isVessel, normIcon } from "../../lib/icons";
import {
  ALL_ITEMS, DEFAULT_ITEMS, KIND_LABEL, MOTOR_KINDS, MOTOR_SPECS, PRESET_ITEMS, STATE_LABEL, VESSEL_ITEMS, VESSEL_SPECS,
  fmtSize, itemStatus, type Doc, type Item, type ItemState, type Vehicle,
} from "../../lib/fleet";
import { Drawer, Field, Notice } from "../admin/ui";

export type FleetDev = { id: string; name: string; icon?: string | null };
type EditItem = Partial<Item> & { key: string; _delete?: boolean };
type Category = "motor" | "vessel";

const BLANK = {
  reg: "", fleet_no: "", kind: "vehicle", make_model: "", color: "", year: "", engine_no: "", engine_cc: "", chassis_vin: "",
  acquired_on: "", status: "active", fuel_card: "", project: "", notes: "", device_id: "",
};
type Form = typeof BLANK;
const MAX_DOC = 15 * 1024 * 1024;
let keySeq = 0;
const toEdit = (it: Item): EditItem => ({ ...it, key: it.id });
const presetItems = (c: Category): EditItem[] => DEFAULT_ITEMS[c].map(([kind, label]) => ({ key: `n${keySeq++}`, kind, label, state: "date" }));

/**
 * Vehicle / vessel record: details, renewals (compliance items) and documents.
 * New records start from a tracker (or "no tracker fitted"), then motor vehicle vs marine vessel.
 * Loads its own copy so a newly created record can take documents straight away.
 */
export default function VehicleDrawer({ vehicleId, org, devices, linked, editable, warn, presetDevice, onClose, onChanged }: {
  vehicleId: string | null; org: string; devices: FleetDev[]; linked: string[]; editable: boolean; warn: number;
  /** Start a new record from this tracker (skips the "choose a tracker" step). */
  presetDevice?: string | null;
  onClose: () => void; onChanged: () => void;
}) {
  const [id, setId] = useState(vehicleId);
  const [v, setV] = useState<Vehicle | null>(null);
  const [step, setStep] = useState<"pick" | "form">(vehicleId ? "form" : "pick");
  const [cat, setCat] = useState<Category>("motor");
  const [f, setF] = useState<Form>(BLANK);
  const [specs, setSpecs] = useState<Record<string, string>>({});
  const [items, setItems] = useState<EditItem[]>(presetItems("motor"));
  const [itemsTouched, setItemsTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async (vid: string) => {
    const { data, error } = await supabase.from("vehicles")
      .select("*, compliance_items(*), vehicle_documents(id, vehicle_id, item_id, name, mime, size_bytes, uploaded_at)").eq("id", vid).maybeSingle();
    if (error || !data) { setErr(error?.message ?? "Record not found"); return; }
    const x = data as Vehicle;
    x.compliance_items.sort((a, b) => order(a.kind) - order(b.kind) || a.label.localeCompare(b.label));
    x.vehicle_documents.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    setV(x);
    setCat(x.kind === "vessel" ? "vessel" : "motor");
    setF(Object.fromEntries(Object.keys(BLANK).map((k) => [k, (x as any)[k] == null ? "" : String((x as any)[k])])) as Form);
    setSpecs(x.specs ?? {});
    setItems(x.compliance_items.map(toEdit));
    setDirty(false);
  }, []);
  useEffect(() => { if (id) load(id); }, [id, load]);
  useEffect(() => { // opened from a "not in the fleet yet" suggestion
    if (!vehicleId && presetDevice) { const d = devices.find((x) => x.id === presetDevice); if (d) startFrom(d); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof Form) => (e: { target: { value: string } }) => { setF({ ...f, [k]: e.target.value }); setDirty(true); };
  const setSpec = (k: string) => (e: { target: { value: string } }) => { setSpecs({ ...specs, [k]: e.target.value }); setDirty(true); };
  const setItem = (key: string, patch: Partial<EditItem>) => { setItems((all) => all.map((i) => (i.key === key ? { ...i, ...patch } : i))); setDirty(true); setItemsTouched(true); };
  const freeDevices = devices.filter((d) => !linked.includes(d.id) || d.id === v?.device_id);
  const vessel = cat === "vessel";

  function chooseCategory(c: Category) {
    setCat(c);
    setF((cur) => ({ ...cur, kind: c === "vessel" ? "vessel" : cur.kind === "vessel" ? "vehicle" : cur.kind }));
    if (!id && !itemsTouched) setItems(presetItems(c)); // new record: swap the default renewal items
    setDirty(true);
  }

  function startFrom(dev: FleetDev | null) {
    const c: Category = isVessel(dev?.icon) ? "vessel" : "motor";
    setF({ ...BLANK, device_id: dev?.id ?? "", kind: c === "vessel" ? "vessel" : dev?.icon === "machinery" ? "heavy" : "vehicle", fleet_no: "" });
    setCat(c);
    setItems(presetItems(c));
    setStep("form");
    setDirty(true);
  }

  async function save(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try {
      const keys = (vessel ? VESSEL_SPECS : MOTOR_SPECS).map(([k]) => k);
      const body: Record<string, unknown> = {
        ...f, device_id: f.device_id || null, kind: vessel ? "vessel" : f.kind === "vessel" ? "vehicle" : f.kind,
        specs: Object.fromEntries(Object.entries(specs).filter(([k, val]) => keys.includes(k) && String(val).trim())),
        ...(vessel ? { engine_cc: "" } : {}),
      };
      const payloadItems = items.filter((i) => i.id || !i._delete).map(({ key, vehicle_id, org_id, ...rest }) => rest);
      if (id) {
        await orgApi("PATCH", `vehicles/${id}`, body);
        await orgApi("PUT", `vehicles/${id}/items`, { items: payloadItems });
        await load(id);
        setMsg("Saved.");
      } else {
        const created = await orgApi<Vehicle>("POST", "vehicles", { ...body, org_id: org, items: payloadItems });
        setId(created.id);
        setMsg(`${vessel ? "Vessel" : "Vehicle"} added — you can now attach documents.`);
      }
      onChanged();
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }

  async function remove() {
    if (!id || !v) return;
    if (!confirm(`Delete ${v.reg} and all its documents? This cannot be undone.\n\nTip: set the status to "Cancelled" instead to keep the history.`)) return;
    setBusy(true);
    try { await orgApi("DELETE", `vehicles/${id}`); onChanged(); onClose(); } catch (x: any) { setErr(x.message); setBusy(false); }
  }

  function addItem(kind: string) {
    const preset = ALL_ITEMS.find(([k]) => k === kind);
    setItems((all) => [...all, { key: `n${keySeq++}`, kind: preset ? kind : "custom", label: preset ? preset[1] : "", state: "date" }]);
    setDirty(true); setItemsTouched(true);
  }
  const usedKinds = new Set(items.filter((i) => !i._delete).map((i) => i.kind));
  const offer = vessel ? VESSEL_ITEMS : PRESET_ITEMS;
  const what = vessel ? "vessel" : "vehicle";
  const title = id ? (v ? `${v.reg}${v.make_model ? ` — ${v.make_model}` : ""}` : "Loading…") : step === "pick" ? "Add to fleet" : `Add ${what}`;
  const tracker = devices.find((d) => d.id === f.device_id);

  if (step === "pick") {
    return (
      <Drawer title={title} onClose={onClose} footer={<button className="btn-ghost" onClick={onClose}>Cancel</button>}>
        <p className="sub">Start from the tracker fitted to it — its live position, history and alerts will then show on the fleet record.</p>
        <h3 className="drawer-sub">Choose a tracker</h3>
        <ul className="dev-pick">
          {freeDevices.map((d) => (
            <li key={d.id}><button type="button" onClick={() => startFrom(d)}>
              <span className="licon stopped" dangerouslySetInnerHTML={{ __html: iconSvg(d.icon ?? null, 15) }} />
              <b>{d.name}</b><span className="sub">{isVessel(d.icon) ? "Boat" : normIcon(d.icon) === "machinery" ? "Machinery" : ""}</span><span className="go">›</span>
            </button></li>
          ))}
          {!freeDevices.length && <li className="sub">All of this company's trackers are already linked to a fleet record.</li>}
        </ul>
        <button type="button" className="linkish nontracked" onClick={() => startFrom(null)}>No tracker fitted — add untracked equipment instead</button>
      </Drawer>
    );
  }

  return (
    <Drawer title={title} onClose={() => { if (!dirty || confirm("Discard unsaved changes?")) onClose(); }}
      footer={editable ? (
        <>
          {id && <button className="btn-ghost danger" onClick={remove} disabled={busy} style={{ marginRight: "auto" }}>Delete</button>}
          {!id && <button className="btn-ghost" onClick={() => setStep("pick")} style={{ marginRight: "auto" }}>‹ Tracker</button>}
          <button className="btn-ghost" onClick={onClose}>Close</button>
          <button className="btn-primary" form="veh-form" disabled={busy || (!!id && !dirty)}>{busy ? "Saving…" : id ? "Save changes" : `Add ${what}`}</button>
        </>
      ) : <button className="btn-ghost" onClick={onClose}>Close</button>}>
      {msg && <Notice onClose={() => setMsg(null)}>{msg}</Notice>}
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}

      <form id="veh-form" className="aform" onSubmit={save}>
        <fieldset disabled={!editable} className="aform" style={{ border: 0, padding: 0, margin: 0, display: "contents" }}>
          <div className="wide cat-toggle" role="group" aria-label="Category">
            <button type="button" className={!vessel ? "on" : ""} onClick={() => chooseCategory("motor")}>🚙 Motor vehicle</button>
            <button type="button" className={vessel ? "on" : ""} onClick={() => chooseCategory("vessel")}>⛴ Marine vessel</button>
          </div>
          <Field label="Tracker" wide hint={!f.device_id ? "No tracker — this record won't show on the map." : undefined}>
            <select value={f.device_id} onChange={set("device_id")}>
              <option value="">— no tracker fitted —</option>
              {freeDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>

          <h3 className="drawer-sub wide">{vessel ? "Vessel" : "Vehicle"}</h3>
          <Field label={vessel ? "Registration / MSAF no. *" : "Registration *"}>
            <input value={f.reg} onChange={set("reg")} required maxLength={30} placeholder={vessel ? "e.g. MSAF 1234" : tracker ? `e.g. KM 123 (${tracker.name})` : "e.g. KM 123"} className="mono" />
          </Field>
          <Field label="Fleet number"><input value={f.fleet_no} onChange={set("fleet_no")} maxLength={30} /></Field>
          {!vessel && (
            <Field label="Type">
              <select value={f.kind} onChange={set("kind")}>{MOTOR_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select>
            </Field>
          )}
          <Field label="Status">
            <select value={f.status} onChange={set("status")}><option value="active">In service</option><option value="cancelled">Cancelled / disposed</option></select>
          </Field>
          <Field label={vessel ? "Vessel name / model" : "Make & model"} wide>
            <input value={f.make_model} onChange={set("make_model")} maxLength={80} placeholder={vessel ? "e.g. MV Bula Star — Stabicraft 2100" : "e.g. Toyota Hilux 2.8 D/C"} />
          </Field>
          <Field label={vessel ? "Hull colour" : "Colour"}><input value={f.color} onChange={set("color")} maxLength={80} /></Field>
          <Field label={vessel ? "Year built" : "Year"}><input value={f.year} onChange={set("year")} inputMode="numeric" maxLength={4} /></Field>
          {!vessel && <>
            <Field label="Engine number"><input value={f.engine_no} onChange={set("engine_no")} maxLength={80} className="mono" /></Field>
            <Field label="Engine size (cc)"><input value={f.engine_cc} onChange={set("engine_cc")} inputMode="numeric" /></Field>
            <Field label="Chassis / VIN" wide><input value={f.chassis_vin} onChange={set("chassis_vin")} maxLength={80} className="mono" /></Field>
          </>}
          <Field label="Acquired"><input type="date" value={f.acquired_on} onChange={set("acquired_on")} /></Field>
          <Field label={vessel ? "Project / base" : "Project / location"}><input value={f.project} onChange={set("project")} maxLength={80} /></Field>
          <Field label="Fuel card number" hint="Card number only — never store the PIN."><input value={f.fuel_card} onChange={set("fuel_card")} maxLength={80} className="mono" /></Field>

          <h3 className="drawer-sub wide">{vessel ? "Hull & engine details" : "More details"}</h3>
          {(vessel ? VESSEL_SPECS : MOTOR_SPECS).map(([k, label, ph]) => (
            <Field key={k} label={label} wide={k === "engine_make" || k === "engine_serials" || k === "home_port"}>
              <input value={specs[k] ?? ""} onChange={setSpec(k)} maxLength={120} placeholder={ph} className={/serial|hull_id|mmsi|callsign/.test(k) ? "mono" : undefined} />
            </Field>
          ))}
          <Field label="Notes" wide><textarea rows={2} value={f.notes} onChange={set("notes")} maxLength={4000} /></Field>

          <h3 className="drawer-sub wide">{vessel ? "Licences, surveys, insurance & maintenance" : "Registration, insurance & renewals"}</h3>
          <div className="wide items-edit">
            {items.filter((i) => !i._delete).map((it) => {
              const s = itemStatus(it as Item, warn);
              const docs = v?.vehicle_documents.filter((d) => it.id && d.item_id === it.id).length ?? 0;
              return (
                <div key={it.key} className={`item-row t-${s.tier}`}>
                  <div className="item-head">
                    <input className="item-label" value={it.label ?? ""} onChange={(e) => setItem(it.key, { label: e.target.value })} placeholder="Item name" maxLength={60} required />
                    <span className={`tier-txt t-${s.tier}`}>{s.text}{s.sub && ` · ${s.sub}`}</span>
                    {docs > 0 && <span className="sub">📎 {docs}</span>}
                    {editable && <button type="button" className="x" aria-label="Remove item" onClick={() => setItem(it.key, { _delete: true })}>×</button>}
                  </div>
                  <div className="item-grid">
                    <select value={it.state} onChange={(e) => setItem(it.key, { state: e.target.value as ItemState })}>
                      {Object.entries(STATE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <input type="date" value={it.expires_on ?? ""} disabled={it.state !== "date"} onChange={(e) => setItem(it.key, { expires_on: e.target.value || null })} aria-label="Expiry date" />
                    <input value={it.provider ?? ""} onChange={(e) => setItem(it.key, { provider: e.target.value })} placeholder={vessel ? "Provider / insurer / workshop" : "Provider / insurer"} maxLength={80} />
                    <input value={it.reference ?? ""} onChange={(e) => setItem(it.key, { reference: e.target.value })} placeholder={vessel ? "Licence / policy / ref no." : "Policy / ref no."} maxLength={80} className="mono" />
                  </div>
                </div>
              );
            })}
            {editable && (
              <select className="add-item" value="" onChange={(e) => e.target.value && addItem(e.target.value)}>
                <option value="">+ Add renewal item…</option>
                {offer.filter(([k]) => !usedKinds.has(k)).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                <option value="custom">Custom item…</option>
              </select>
            )}
          </div>
        </fieldset>
      </form>

      <h3 className="drawer-sub">Documents</h3>
      {!id ? <p className="sub">Save the {what} first, then attach {vessel ? "MSAF licences, survey reports, insurance certificates and service records" : "registration papers, insurance certificates and permits"}.</p> : v && (
        <Documents vehicle={v} editable={editable} onChanged={async () => { await load(v.id); onChanged(); }} onError={setErr} />
      )}
    </Drawer>
  );
}

const order = (k: string) => { const i = ALL_ITEMS.findIndex(([p]) => p === k); return i < 0 ? 99 : i; };

function Documents({ vehicle, editable, onChanged, onError }: {
  vehicle: Vehicle; editable: boolean; onChanged: () => Promise<void>; onError: (m: string) => void;
}) {
  const [itemId, setItemId] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const label = (d: Doc) => vehicle.compliance_items.find((i) => i.id === d.item_id)?.label;

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_DOC) { onError(`${file.name} is larger than 15 MB`); continue; }
      setUploading(file.name);
      try {
        const u = await orgApi<{ path: string; token: string }>("POST", `vehicles/${vehicle.id}/documents/upload-url`, { name: file.name, size: file.size, mime: file.type });
        const { error } = await supabase.storage.from("fleet-docs").uploadToSignedUrl(u.path, u.token, file, { contentType: file.type || undefined });
        if (error) throw new Error(error.message);
        await orgApi("POST", `vehicles/${vehicle.id}/documents`, { path: u.path, name: file.name, mime: file.type, size: file.size, item_id: itemId || null });
      } catch (x: any) { onError(`${file.name}: ${x.message}`); }
    }
    setUploading(null);
    if (input.current) input.current.value = "";
    await onChanged();
  }

  async function open(d: Doc, download: boolean) {
    // Open the tab synchronously so pop-up blockers allow it, then point it at the signed URL.
    const w = download ? null : window.open("about:blank", "_blank");
    try {
      const { url } = await orgApi<{ url: string }>("GET", `documents/${d.id}/url${download ? "?download=1" : ""}`);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (x: any) { w?.close(); onError(x.message); }
  }

  async function del(d: Doc) {
    if (!confirm(`Delete ${d.name}?`)) return;
    try { await orgApi("DELETE", `documents/${d.id}`); await onChanged(); } catch (x: any) { onError(x.message); }
  }

  return (
    <div className="docs">
      {editable && (
        <div className="doc-upload">
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} aria-label="Attach to">
            <option value="">General document</option>
            {vehicle.compliance_items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
          <input ref={input} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx" onChange={(e) => upload(e.target.files)} />
          <button type="button" className="btn-ghost" disabled={!!uploading} onClick={() => input.current?.click()}>{uploading ? `Uploading ${uploading}…` : "Upload files…"}</button>
          <span className="sub">PDF, images, Word or Excel · up to 15 MB each</span>
        </div>
      )}
      {!vehicle.vehicle_documents.length ? <p className="sub">No documents attached.</p> : (
        <ul className="doc-list">
          {vehicle.vehicle_documents.map((d) => (
            <li key={d.id}>
              <span className="doc-ico">{/pdf/.test(d.mime ?? "") ? "PDF" : /image/.test(d.mime ?? "") ? "IMG" : "DOC"}</span>
              <div className="doc-name">
                <b title={d.name}>{d.name}</b>
                <span className="sub">{[label(d), fmtSize(d.size_bytes), new Date(d.uploaded_at).toLocaleDateString("en-GB")].filter(Boolean).join(" · ")}</span>
              </div>
              <button type="button" className="btn-ghost small" onClick={() => open(d, false)}>View</button>
              <button type="button" className="btn-ghost small" onClick={() => open(d, true)}>Download</button>
              {editable && <button type="button" className="x" aria-label={`Delete ${d.name}`} onClick={() => del(d)}>×</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
